import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import PageHeader from "@/shared/components/PageHeader";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import { PathPickOrderDetailBody } from "@/features/pathpick/features/order-history/features/detail";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import useScaleToFit from "@/features/pathpick/features/start-guide/hooks/useScaleToFit";
import { StartGuideDemoState } from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";
import { StartGuideSalesMode } from "@/features/pathpick/features/start-guide/types";
import {
  DEMO_ORDER,
  DEMO_REFUND_ACCOUNT,
} from "@/features/pathpick/features/start-guide/utils/demoOrder";

interface Props {
  demo: StartGuideDemoState;
  // KREAM 흐름만 파일 신청을 체험하는 화면이라 강조와 잠금이 붙는다.
  salesMode: StartGuideSalesMode;
  // `신청 후 이용` 탭 안에서 이 화면이 몇 번째인지 — 단계 표시와 이동 라벨이 여기서 갈린다.
  stepIndex: number;
  stepTotal: number;
  onRequestKreamFile: () => void;
  onPrev: () => void;
  onNext: () => void;
}

// 실제 주문 상세 본문의 최대 폭. 데스크톱 사용자가 보는 폭 그대로 그린 뒤 축소한다.
const ORDER_DETAIL_DESIGN_WIDTH = 1120;

const NEXT_HINT_ID = "start-guide-order-detail-next-hint";

const ORDER_DETAIL_GUIDE_KEY = "path_pick_guide:order_detail.";

/**
 * 주문 상세 안내 — 판매 방식에 따라 두 가지 얼굴을 갖는다.
 *
 * 요약본이 아니라 실제 주문 상세 화면(`PathPickOrderDetailBody`)을 목 주문으로 그대로 렌더한다.
 * 목 주문(`DEMO_ORDER`)은 입금과 수량 확정까지 끝난 고정 샘플이라, 앞 화면에서 무엇을
 * 몇 개 주문했는지와 무관하게 같은 내용을 보여준다.
 *
 * 스마트스토어(API·엑셀): 주문 뒤에 무엇을 확인할 수 있는지 읽는 화면이다. 진행 단계·입금·배송·
 * 확정 수량이 한자리에 모인 것을 보여주는 것이 목적이라 아무것도 잠그지 않는다(전달서 §13).
 * KREAM 파일 카드는 실제 화면에도 있으니 감추지 않되, 강조도 하지 않는다.
 *
 * KREAM 전용: 파일 신청 위치를 보여주는 것이 목적이라 그 카드만 강조·클릭 가능하게 살리고,
 * 신청을 마쳐야 다음으로 넘어간다.
 *
 * 두 얼굴 모두 화면 전체의 클릭을 막고 주문 상품 표만 되살린다 — 표는 좌우 스크롤로 읽어야 한다.
 */
function PathPickStartGuideOrderDetailTour({
  demo,
  salesMode,
  stepIndex,
  stepTotal,
  onRequestKreamFile,
  onPrev,
  onNext,
}: Props) {
  const { t } = useI18nHelper();
  const { frameRef, contentRef, scale, scaledHeight } = useScaleToFit(
    ORDER_DETAIL_DESIGN_WIDTH
  );

  const isKreamOnly = salesMode === "kream";
  // KREAM 흐름만 파일 생성을 신청해 봐야 이 화면의 체험이 끝난다.
  const canGoNext = !isKreamOnly || demo.hasRequestedKreamFile;

  // 탭의 양 끝에서만 탭 밖으로 나간다 — 그때만 `단계` 라벨을 쓴다.
  const isFirstPage = stepIndex === 0;
  const isLastPage = stepIndex === stepTotal - 1;

  const key = (base: string) =>
    `${ORDER_DETAIL_GUIDE_KEY}${base}${isKreamOnly ? "_kream" : ""}`;

  return (
    <PathPickStartGuideCard
      header={
        <Flex flexDirection={"column"} gap={"6px"}>
          <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t(key("title"))}
            </Text>
            {/* 판매 방식에 따라 이 탭의 화면 수가 달라진다 — 한 장뿐이면 표시하지 않는다. */}
            {stepTotal > 1 && (
              <Text
                textStyle={"caption-bold"}
                color={"#1272EF"}
                role={"status"}
                aria-label={t("path_pick_guide:nav.step_aria_label")}
              >
                {t("path_pick_guide:nav.step_label", {
                  current: stepIndex + 1,
                  total: stepTotal,
                })}
              </Text>
            )}
          </Flex>
          <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
            {t(key("description"))}
          </Text>
        </Flex>
      }
      headerProps={{
        paddingBottom: "0px",
      }}
    >
      <Flex flexDirection={"column"} gap={"16px"}>
        {/* 이동 버튼은 이 단계 패널의 우측 하단에 붙는다 — 다른 탭과 같다. */}
        <PathPickStartGuideStepPanel
          // KREAM 은 바로 아래 본문이 할 일을 그대로 적어 제목이 겹친다 — 경로 칩만 남긴다.
          title={
            isKreamOnly ? undefined : t(`${ORDER_DETAIL_GUIDE_KEY}step_title`)
          }
          titleBadge={
            <PathPickBadge
              label={t(`${ORDER_DETAIL_GUIDE_KEY}path_label`)}
              colorVariant={"gray"}
              size={"medium"}
            />
          }
          description={t(key("body"))}
          actions={
            <Flex flexDirection={"column"} gap={"6px"} alignItems={"flex-end"}>
              <Flex gap={"8px"}>
                <AppButton variant={"normal"} onClick={onPrev}>
                  {t(
                    isFirstPage
                      ? "path_pick_guide:nav.prev_step"
                      : "path_pick_guide:nav.prev"
                  )}
                </AppButton>
                <AppButton
                  variant={"primary"}
                  isDisabled={!canGoNext}
                  onClick={onNext}
                  chakraProps={
                    canGoNext ? undefined : { "aria-describedby": NEXT_HINT_ID }
                  }
                >
                  {t(
                    isLastPage
                      ? "path_pick_guide:nav.next_step"
                      : "path_pick_guide:nav.next"
                  )}
                </AppButton>
              </Flex>
              {!canGoNext && (
                <PathPickStartGuideDisabledHint
                  id={NEXT_HINT_ID}
                  textAlign={"right"}
                >
                  {t(`${ORDER_DETAIL_GUIDE_KEY}next_disabled_hint`)}
                </PathPickStartGuideDisabledHint>
              )}
            </Flex>
          }
        />

        {/* 완료 안내는 이동 버튼 바로 아래에 둔다 — 신청 결과를 프레임까지 내려가지 않고 본다. */}
        {isKreamOnly && demo.hasRequestedKreamFile && (
          <Flex
            role={"status"}
            flexDirection={"column"}
            gap={"4px"}
            padding={"12px 14px"}
            borderRadius={"6px"}
            border={"0.5px solid #A9E0AB"}
            backgroundColor={"#E9F7EF"}
          >
            <Text textStyle={"body-sm-bold"} color={"#247A52"}>
              {t(`${ORDER_DETAIL_GUIDE_KEY}completed`)}
            </Text>
            <Text textStyle={"caption-medium"} color={"#247A52"}>
              {t(`${ORDER_DETAIL_GUIDE_KEY}completed_notice`)}
            </Text>
          </Flex>
        )}

        {/* 실제 화면을 그대로 담는 프레임 — 제목 바까지 포함해 페이지 모양을 재현한다. */}
        <Box
          borderRadius={"12px"}
          border={"0.5px solid #D4D4D1"}
          overflow={"hidden"}
          backgroundColor={"#FFFFFF"}
        >
          {/* onIconClick 을 주지 않아 뒤로가기는 눌러도 아무 일이 없다. */}
          <PageHeader
            iconName={"chevron-left"}
            iconSize={28}
            title={t(`${ORDER_DETAIL_KEY}title`)}
            color={"#1D1D1B"}
          />
          <Box
            ref={frameRef}
            height={`${scaledHeight}px`}
            overflow={"hidden"}
            // 주문 상품 카드는(KREAM 흐름에서는 파일 카드도) 각자 pointer-events 를 되살린다.
            pointerEvents={"none"}
          >
            <Box
              ref={contentRef}
              width={`${ORDER_DETAIL_DESIGN_WIDTH}px`}
              transformOrigin={"top left"}
              transform={`scale(${scale})`}
              // 실제 페이지의 스크롤 영역 배경. 가이드에서는 스크롤 없이 전체를 펼치므로
              // 하단 여백(paddingBottom 100px)은 두지 않는다.
              padding={"16px"}
              backgroundColor={"#F7F9FA"}
            >
              <PathPickOrderDetailBody
                order={DEMO_ORDER}
                preview={{
                  refundAccount: DEMO_REFUND_ACCOUNT,
                  // 미리보기는 데모 주문이라 실제 신청을 보내지 않는다 —
                  // 신청했다는 사실만 기억해 두고 위 패널로 알린다.
                  // 스마트스토어 흐름에서는 콜백을 주지 않아 파일 카드가 강조 없이 잠긴다.
                  onKreamFileRequested: isKreamOnly
                    ? onRequestKreamFile
                    : undefined,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Flex>
    </PathPickStartGuideCard>
  );
}

export default PathPickStartGuideOrderDetailTour;
